/* =====================================================================
 * @auw/feature-flags — React context & hook.
 *
 * Mounts once near the application root (immediately inside the tenant
 * provider so flags are always resolved against the active manifest).
 * The `isEnabled` closure is stable across renders as long as the
 * underlying flag object reference is stable — callers can safely pass
 * it to `.filter()` and memoised predicates.
 * ===================================================================== */

import React, { createContext, useContext, useMemo } from 'react';
import type { FeatureFlagMap, FeatureId } from './types';

export interface FeatureFlagContextValue {
  readonly flags: FeatureFlagMap;
  readonly isEnabled: (id: FeatureId) => boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

export interface FeatureFlagProviderProps {
  /** Resolved flag map for the active tenant. `undefined` → every flag disabled. */
  flags?: FeatureFlagMap;
  children: React.ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  flags,
  children,
}) => {
  const value = useMemo<FeatureFlagContextValue>(() => {
    const resolved: FeatureFlagMap = flags ?? {};
    const isEnabled = (id: FeatureId): boolean => Boolean(resolved[id]);
    return { flags: resolved, isEnabled };
  }, [flags]);

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Access the resolved flag map for the current tenant. Throws when
 * called outside a `<FeatureFlagProvider>` so misconfigurations fail
 * loudly instead of silently returning stale `false`s.
 */
export function useFeatureFlags(): FeatureFlagContextValue {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    throw new Error(
      '[feature-flags] useFeatureFlags must be used inside a <FeatureFlagProvider>.',
    );
  }
  return ctx;
}

/**
 * Sugar hook for the common single-flag case:
 *   `const canEditPlugins = useFeature('page.plugin');`
 */
export function useFeature(id: FeatureId): boolean {
  return useFeatureFlags().isEnabled(id);
}
